// client/src/pages/admin/Maintenance.tsx
import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Statistic,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Calendar,
  Badge
} from 'antd';
import {
  ToolOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CarOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { maintenanceService, MaintenanceRecord, CreateMaintenanceData, MaintenanceStats } from '../../services/maintenanceService';
import apiClient from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AdminMaintenance: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MaintenanceRecord | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Récupération des maintenances
  const { data: maintenancesData, isLoading } = useQuery({
    queryKey: ['maintenance', searchText, filterType],
    queryFn: async () => {
      const params: any = {};
      if (searchText) params.search = searchText;
      if (filterType) params.type = filterType;

      // ✅ Le service retourne déjà la bonne structure
      const response = await maintenanceService.getMaintenances(params);
      return response;
    },
  });

  // Récupération des statistiques
  const { data: statsData } = useQuery({
    queryKey: ['maintenance-stats'],
    queryFn: async () => {
      // ✅ Le service retourne déjà la bonne structure
      const response = await maintenanceService.getMaintenanceStats();
      return response;
    },
  });

  // Récupération des véhicules pour le formulaire
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles-list'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/vehicles');
      return response.data;
    },
  });

  const maintenanceRecords = maintenancesData?.maintenance || [];
  const stats: MaintenanceStats | undefined = statsData;
  const vehicles = vehiclesData?.vehicles || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateMaintenanceData) => maintenanceService.createMaintenance(data),
    onSuccess: () => {
      message.success('Maintenance créée avec succès');
      setModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la création');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateMaintenanceData> }) =>
      maintenanceService.updateMaintenance(id, data),
    onSuccess: () => {
      message.success('Maintenance modifiée avec succès');
      setModalVisible(false);
      setEditingRecord(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la modification');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => maintenanceService.deleteMaintenance(id),
    onSuccess: () => {
      message.success('Maintenance supprimée avec succès');
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    },
  });

  // Handlers
  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: MaintenanceRecord) => {
    setEditingRecord(record);
    form.setFieldsValue({
      vehicle_id: record.vehicle_id,
      date: dayjs(record.date),
      type: record.type,
      description: record.description,
      cost: record.cost,
      mileage: record.mileage,
      next_maintenance_date: record.next_maintenance_date ? dayjs(record.next_maintenance_date) : null,
      notes: record.notes
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = async (values: any) => {
    const formattedData = {
      ...values,
      date: values.date.format('YYYY-MM-DD'),
      next_maintenance_date: values.next_maintenance_date?.format('YYYY-MM-DD') || null
    };

    if (editingRecord) {
      updateMutation.mutate({ id: editingRecord.id, data: formattedData });
    } else {
      createMutation.mutate(formattedData);
    }
  };

  // Fonctions utilitaires
  const getTypeColor = (type: string) => {
    const colors = {
      routine: 'blue',
      repair: 'orange',
      inspection: 'green',
      emergency: 'red'
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  const getTypeText = (type: string) => {
    const texts = {
      routine: 'Routine',
      repair: 'Réparation',
      inspection: 'Inspection',
      emergency: 'Urgence'
    };
    return texts[type as keyof typeof texts] || type;
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      routine: <CalendarOutlined />,
      repair: <ToolOutlined />,
      inspection: <CheckCircleOutlined />,
      emergency: <ExclamationCircleOutlined />
    };
    return icons[type as keyof typeof icons] || <ToolOutlined />;
  };

  // Colonnes du tableau
  const columns: ColumnsType<MaintenanceRecord> = [
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (_, record) => (
        <Space>
          <CarOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.vehicle_plate}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.vehicle_brand} {record.vehicle_model}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => (
        <Space>
          {getTypeIcon(record.type)}
          <Tag color={getTypeColor(record.type)}>
            {getTypeText(record.type)}
          </Tag>
        </Space>
      ),
      filters: [
        { text: 'Routine', value: 'routine' },
        { text: 'Réparation', value: 'repair' },
        { text: 'Inspection', value: 'inspection' },
        { text: 'Urgence', value: 'emergency' }
      ],
      onFilter: (value, record) => record.type === value
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div style={{ maxWidth: '300px' }}>
          {text}
        </div>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    },
    {
      title: 'Coût',
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => cost ? `${cost.toFixed(2)} €` : '-',
      sorter: (a, b) => (a.cost || 0) - (b.cost || 0)
    },
    {
      title: 'Kilométrage',
      dataIndex: 'mileage',
      key: 'mileage',
      render: (mileage: number) => mileage ? `${mileage.toLocaleString()} km` : '-'
    },
    {
      title: 'Prochaine maintenance',
      dataIndex: 'next_maintenance_date',
      key: 'next_maintenance_date',
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Modifier
          </Button>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette maintenance ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Gestion de la Maintenance</Title>
        <Text type="secondary">Gérez les maintenances de votre flotte de véhicules</Text>
      </div>

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total maintenances"
              value={stats?.total_maintenance || 0}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Ce mois"
              value={stats?.last_30_days_count || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût total"
              value={stats?.total_cost || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="€"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût moyen"
              value={stats?.average_cost || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="€"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tableau principal */}
      <Card
        title={
          <Space>
            <ToolOutlined />
            Liste des maintenances
            <Badge count={maintenanceRecords.length} style={{ backgroundColor: '#52c41a' }} />
          </Space>
        }
        extra={
          <Space>
            <Select
              style={{ width: 150 }}
              placeholder="Filtrer par type"
              allowClear
              onChange={setFilterType}
            >
              <Option value="routine">Routine</Option>
              <Option value="repair">Réparation</Option>
              <Option value="inspection">Inspection</Option>
              <Option value="emergency">Urgence</Option>
            </Select>
            <Input.Search
              placeholder="Rechercher..."
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
            />
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setCalendarVisible(true)}
            >
              Planning
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouvelle maintenance
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={maintenanceRecords}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: maintenancesData?.pagination?.total || 0,
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} maintenances`,
          }}
        />
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={editingRecord ? 'Modifier la maintenance' : 'Nouvelle maintenance'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="vehicle_id"
                label="Véhicule"
                rules={[{ required: true, message: 'Sélectionnez un véhicule' }]}
              >
                <Select placeholder="Sélectionnez un véhicule">
                  {vehicles.map((vehicle: any) => (
                    <Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.license_plate} - {vehicle.brand} {vehicle.model}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="date"
                label="Date de maintenance"
                rules={[{ required: true, message: 'Sélectionnez une date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Type de maintenance"
                rules={[{ required: true, message: 'Sélectionnez un type' }]}
              >
                <Select placeholder="Type">
                  <Option value="routine">Routine</Option>
                  <Option value="repair">Réparation</Option>
                  <Option value="inspection">Inspection</Option>
                  <Option value="emergency">Urgence</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="next_maintenance_date"
                label="Prochaine maintenance"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Entrez une description' }]}
          >
            <TextArea rows={3} placeholder="Description détaillée de la maintenance" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cost"
                label="Coût (€)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mileage"
                label="Kilométrage"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes additionnelles"
          >
            <TextArea rows={2} placeholder="Notes supplémentaires..." />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingRecord(null);
                form.resetFields();
              }}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingRecord ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal du planning */}
      <Modal
        title="Planning de maintenance"
        open={calendarVisible}
        onCancel={() => setCalendarVisible(false)}
        footer={null}
        width={800}
      >
        <Calendar
          dateCellRender={(value) => {
            const date = value.format('YYYY-MM-DD');
            const dayRecords = maintenanceRecords.filter((record: MaintenanceRecord) =>
              dayjs(record.date).format('YYYY-MM-DD') === date
            );

            return (
              <div>
                {dayRecords.map((record: MaintenanceRecord) => (
                  <div
                    key={record.id}
                    style={{
                      backgroundColor: getTypeColor(record.type),
                      color: 'white',
                      padding: '2px 4px',
                      borderRadius: '2px',
                      fontSize: '10px',
                      marginBottom: '2px'
                    }}
                  >
                    {record.vehicle_plate}
                  </div>
                ))}
              </div>
            );
          }}
        />
      </Modal>
    </div>
  );
};

export default AdminMaintenance;